import React, { useState } from 'react';
export const BlockSelect = ({ options, value, multiple = false, onChange, onKeyDown, onFocus, readOnly = false, className = '', 'data-fieldname': dataFieldname, variant = 'normal', direction = 'vertical' }) => {
    const [hoveredOption, setHoveredOption] = useState(null);
    // Handle default value based on multiple flag
    const currentValue = value !== undefined ? value : (multiple ? [] : '');
    const isSelected = (optionValue) => {
        if (multiple && Array.isArray(currentValue)) {
            return currentValue.includes(optionValue);
        }
        return currentValue === optionValue;
    };
    const handleOptionClick = (optionValue) => {
        if (readOnly)
            return;
        let newValue;
        if (multiple && Array.isArray(currentValue)) {
            newValue = isSelected(optionValue)
                ? currentValue.filter(v => v !== optionValue)
                : [...currentValue, optionValue];
        }
        else {
            newValue = optionValue;
        }
        // Call onChange with semantic-ui-react compatible signature
        onChange?.(null, { value: newValue });
    };
    const handleKeyDown = (event, optionValue) => {
        // Handle letter keys for quick selection (A, B, C, etc.)
        if (event.key.length === 1 && /[A-Za-z]/.test(event.key)) {
            const letter = event.key.toUpperCase();
            const optionIndex = letter.charCodeAt(0) - 65; // A=0, B=1, etc.
            if (optionIndex >= 0 && optionIndex < options.length) {
                event.preventDefault();
                handleOptionClick(options[optionIndex].value);
                return;
            }
        }
        if (optionValue && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            handleOptionClick(optionValue);
        }
        onKeyDown?.(event);
    };
    const getOptionLetter = (index) => {
        return String.fromCharCode(65 + index); // A, B, C, etc.
    };
    // Dynamic styles based on variant and direction
    const getContainerClasses = () => {
        const baseClasses = "block-select";
        const variantClasses = variant === 'compact' ? 'compact-variant' : 'normal-variant';
        const directionClasses = direction === 'horizontal' ? 'horizontal-layout' : 'vertical-layout';
        return `${baseClasses} ${variantClasses} ${directionClasses} ${className}`;
    };
    const getOptionsContainerClasses = () => {
        if (direction === 'horizontal') {
            return variant === 'compact'
                ? "flex flex-wrap gap-2"
                : "flex flex-wrap gap-3";
        }
        return variant === 'compact'
            ? "space-y-2"
            : "space-y-3";
    };
    const getOptionClasses = (isOptionSelected, isHovered) => {
        const baseClasses = `
      group relative cursor-pointer transition-all duration-200 ease-out
      ${readOnly ? 'cursor-not-allowed opacity-60' : ''}
    `;
        if (direction === 'horizontal') {
            return `${baseClasses} ${variant === 'compact' ? 'flex-shrink-0' : 'flex-shrink-0'}`;
        }
        return baseClasses;
    };
    const getOptionContentClasses = (isOptionSelected, isHovered) => {
        const baseTransition = "flex items-center rounded-xl border-2 transition-all duration-200";
        // Size based on variant
        const padding = variant === 'compact' ? 'p-2' : 'p-4';
        // Colors based on selection state
        const colorClasses = isOptionSelected
            ? 'fluid-dropdown fluid-input bg-orange-50  transform scale-[1.02]'
            : isHovered
                ? 'fluid-dropdown fluid-input transform scale-[1.01]'
                : 'fluid-dropdown fluid-input';
        return `${baseTransition} ${padding} ${colorClasses}`;
    };
    const getLetterIndicatorClasses = (isOptionSelected, isHovered) => {
        const baseClasses = "flex items-center justify-center rounded-lg text-sm font-bold transition-colors duration-200";
        // Size based on variant
        const sizeClasses = variant === 'compact' ? 'w-6 h-6 mr-2' : 'w-8 h-8 mr-4';
        // Colors based on selection state
        const colorClasses = isOptionSelected
            ? 'bg-orange-500 text-white'
            : isHovered
                ? 'bg-orange-200 text-orange-700'
                : 'bg-gray-100 text-gray-600';
        return `${baseClasses} ${sizeClasses} ${colorClasses}`;
    };
    return (React.createElement("div", { className: getContainerClasses(), "data-fieldname": dataFieldname, onKeyDown: (e) => handleKeyDown(e), onFocus: onFocus, tabIndex: 0 },
        React.createElement("div", { className: getOptionsContainerClasses() }, options.map((option, index) => {
            const isOptionSelected = isSelected(option.value);
            const isHovered = hoveredOption === option.value;
            return (React.createElement("div", { key: option.key, className: getOptionClasses(isOptionSelected, isHovered), onClick: () => handleOptionClick(option.value), onKeyDown: (e) => handleKeyDown(e, option.value), onMouseEnter: () => setHoveredOption(option.value), onMouseLeave: () => setHoveredOption(null), tabIndex: -1, role: "button", "aria-pressed": isOptionSelected },
                React.createElement("div", { className: getOptionContentClasses(isOptionSelected, isHovered) },
                    React.createElement("div", { className: getLetterIndicatorClasses(isOptionSelected, isHovered) }, getOptionLetter(index)),
                    React.createElement("div", { className: "flex-1" },
                        React.createElement("div", { className: `
                      font-medium transition-colors duration-200
                      ${variant === 'compact' ? 'text-sm' : 'text-base'}
                      ${isOptionSelected
                                ? 'text-orange-900'
                                : 'text-gray-800'}
                    ` }, option.text),
                        option.description && variant === 'normal' && (React.createElement("div", { className: `
                        text-sm mt-1 transition-colors duration-200
                        ${isOptionSelected
                                ? 'text-orange-700'
                                : 'text-gray-500'}
                      ` }, option.description))),
                    isOptionSelected && (React.createElement("div", { className: variant === 'compact' ? 'ml-2' : 'ml-4' },
                        React.createElement("div", { className: `bg-orange-500 rounded-full flex items-center justify-center ${variant === 'compact' ? 'w-4 h-4' : 'w-6 h-6'}` },
                            React.createElement("svg", { className: `text-white ${variant === 'compact' ? 'w-2.5 h-2.5' : 'w-4 h-4'}`, fill: "none", stroke: "currentColor", viewBox: "0 0 24 24" },
                                React.createElement("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M5 13l4 4L19 7" }))))),
                    isHovered && !isOptionSelected && (React.createElement("div", { className: "absolute inset-0 bg-orange-50 opacity-30 rounded-xl pointer-events-none" }))),
                isHovered && !readOnly && variant === 'normal' && direction === 'vertical' && (React.createElement("div", { className: "absolute -right-16 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200" },
                    React.createElement("div", { className: "bg-gray-800 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap" },
                        "Press ",
                        getOptionLetter(index))))));
        })),
        multiple && Array.isArray(currentValue) && currentValue.length > 0 && (React.createElement("div", { className: `text-gray-600 ${variant === 'compact' ? 'mt-2 text-xs' : 'mt-4 text-sm'}` },
            currentValue.length,
            " option",
            currentValue.length !== 1 ? 's' : '',
            " selected"))));
};
export default BlockSelect;
